<script setup lang="ts">
import AccordionComponent from "@/components/accordion/AccordionComponent.vue";
import { defineProps, inject, ref, defineModel, watch } from "vue";
import { DestinyMatrix, DestinyMatrixKeysEnum } from "@/types/destinyMatrix";
import { useI18n } from "vue-i18n";

const props = defineProps<{
  matrix: DestinyMatrix | undefined;
  hasPremiumAccess: boolean;
}>();
const isOpen = defineModel<boolean>({ default: false });
const getDescription =
  inject<(key: DestinyMatrixKeysEnum) => Promise<never>>("getDescription");

const { t } = useI18n();

watch(
  () => props.matrix,
  () => (description.value = undefined)
);

const description = ref();

async function getData() {
  if (!props.hasPremiumAccess) {
    isOpen.value = false;
  }
  if (!description.value && props.matrix && getDescription) {
    description.value = await getDescription(
      DestinyMatrixKeysEnum.MONEY_IN_MATRIX
    );
  }
}
</script>

<template>
  <AccordionComponent
    type="white"
    @opened="getData"
    v-model="isOpen"
    :isBlocked="!hasPremiumAccess"
  >
    <template #title>
      {{ t("calculators.destiny-matrix.accordions.money-in-matrix.title") }}
    </template>
    <template #faq-content>
      <div v-if="description" class="flex flex-col gap-2">
        <div class="whitespace-pre-line">
          {{ t("calculators.destiny-matrix.accordions.money-in-matrix.desc") }}
        </div>
        <p class="text-xl font-bold pt-4">
          {{
            t(
              "calculators.destiny-matrix.accordions.money-in-matrix.suits-professions-title",
              {
                value: matrix.r3.r,
              }
            )
          }}
        </p>
        <div class="whitespace-pre-line">
          {{ description["suits_professions"] }}
        </div>
        <p class="text-xl font-bold pt-4">
          {{
            t(
              "calculators.destiny-matrix.accordions.money-in-matrix.what-gives-money-title",
              {
                value: matrix.right_corner.r,
              }
            )
          }}
        </p>
        <div class="whitespace-pre-line">
          {{ description["what_gives_money"] }}
        </div>
        <div
          v-for="(desc, index) in description[
            'tasks_for_opening_money_channel'
          ]"
          :key="index"
        >
          <p class="text-xl font-bold pt-4">
            {{
              t(
                "calculators.destiny-matrix.accordions.money-in-matrix.tasks-for-opening-money-channel-title",
                {
                  value: index,
                }
              )
            }}
          </p>
          <div class="whitespace-pre-line">
            {{ desc }}
          </div>
        </div>
        <p class="text-xl font-bold pt-4">
          {{
            t(
              "calculators.destiny-matrix.accordions.money-in-matrix.what-blocks-money-title",
              {
                value: matrix.right_corner.c,
              }
            )
          }}
        </p>
        <div class="whitespace-pre-line">
          {{ description["what_blocks_money"] }}
        </div>
      </div>
    </template>
    <template #collapse-text-btn>
      {{ t("calculators.destiny-matrix.accordions.collapse") }}
    </template>
  </AccordionComponent>
</template>
